import { useCallback, useEffect, useRef, useState } from "react";
import { Badge, Button, Card } from "@omnichannel/ui";
import { ApiError, getOwnerReplySessionId, request } from "./api";
import type {
  OwnerMessageItem,
  OwnerMessageListResponse,
  OwnerThreadListResponse,
  OwnerThreadSummary,
} from "./api";

export function buildThreadsPath(
  appId: string,
  limit = 20,
  isOpen?: boolean,
  cursor?: string | null
): string {
  const params = new URLSearchParams();
  params.set("limit", String(limit));
  if (isOpen !== undefined) {
    params.set("is_open", String(isOpen));
  }
  if (cursor) {
    params.set("cursor", cursor);
  }
  return `/api/v1/apps/${encodeURIComponent(appId)}/support/threads/page?${params.toString()}`;
}

export function buildMessagesPath(
  appId: string,
  threadId: string,
  limit = 20,
  cursor?: string | null
): string {
  const params = new URLSearchParams();
  params.set("limit", String(limit));
  if (cursor) {
    params.set("cursor", cursor);
  }
  return `/api/v1/apps/${encodeURIComponent(appId)}/support/threads/${encodeURIComponent(threadId)}/messages/page?${params.toString()}`;
}

export function formatTimestamp(value: unknown): string {
  if (!value || typeof value !== "string") return "—";
  try {
    const d = new Date(value);
    return isNaN(d.getTime()) ? "—" : d.toLocaleString("ru-RU");
  } catch {
    return "—";
  }
}

export function isValidThreadSummary(item: unknown): item is OwnerThreadSummary {
  if (!item || typeof item !== "object" || Array.isArray(item)) return false;
  const t = item as Record<string, unknown>;
  return (
    typeof t.id === "string" &&
    t.id.trim() !== "" &&
    typeof t.subject === "string" &&
    typeof t.external_customer_id === "string" &&
    typeof t.is_open === "boolean" &&
    typeof t.created_at === "string" &&
    !isNaN(new Date(t.created_at).getTime()) &&
    typeof t.updated_at === "string" &&
    !isNaN(new Date(t.updated_at).getTime())
  );
}

export function isValidMessageItem(item: unknown): item is OwnerMessageItem {
  if (!item || typeof item !== "object" || Array.isArray(item)) return false;
  const m = item as Record<string, unknown>;
  return (
    typeof m.id === "string" &&
    m.id.trim() !== "" &&
    typeof m.sender === "string" &&
    typeof m.body === "string" &&
    typeof m.created_at === "string" &&
    !isNaN(new Date(m.created_at).getTime())
  );
}

function formatSender(sender: unknown): string {
  if (sender === "customer") return "Клиент";
  if (sender === "owner") return "Поддержка";
  if (sender === "app") return "Приложение";
  return typeof sender === "string" && sender ? sender : "—";
}

export function validatePageResponse<T>(
  data: unknown,
  currentCursor: string | null | undefined,
  visitedCursors: (string | null)[]
): { items: T[]; next_cursor: string | null; has_more: boolean } {
  if (!data || typeof data !== "object") {
    throw new Error("Некорректный ответ сервера: ожидался объект");
  }
  const raw = data as Record<string, unknown>;
  if (!Array.isArray(raw.items)) {
    throw new Error("Некорректный формат ответа: items должен быть массивом");
  }
  if (typeof raw.has_more !== "boolean") {
    throw new Error("Некорректный формат ответа: has_more должен быть boolean");
  }
  if (raw.next_cursor !== null && typeof raw.next_cursor !== "string") {
    throw new Error("Некорректный формат ответа: next_cursor должен быть string или null");
  }
  if (raw.has_more) {
    if (!raw.next_cursor || typeof raw.next_cursor !== "string" || raw.next_cursor.trim().length === 0) {
      throw new Error("Некорректный формат ответа: при has_more=true отсутствует next_cursor");
    }
    const nextCursor = raw.next_cursor;
    const normCurrent = currentCursor ?? null;
    if (nextCursor === normCurrent || visitedCursors.includes(nextCursor)) {
      throw new Error("Обнаружен зацикленный курсор пагинации");
    }
  }
  return {
    items: raw.items as T[],
    next_cursor: (raw.next_cursor as string | null) ?? null,
    has_more: raw.has_more,
  };
}

type FilterState = "all" | "open" | "closed";

interface PendingReplyOperation {
  ownerSessionId: string;
  appId: string;
  threadId: string;
  idempotencyKey: string;
  body: string;
}

function replyOperationIdentity(operation: PendingReplyOperation): string {
  return `${operation.ownerSessionId}\u0000${operation.appId}\u0000${operation.threadId}\u0000${operation.idempotencyKey}`;
}

const REPLY_OPERATION_STORAGE_PREFIX = "fleet-hub.owner-reply.v1:";
const MAX_PENDING_REPLY_OPERATIONS = 100;

function replyOperationStorageKey(ownerSessionId: string, appId: string, threadId: string): string {
  return `${REPLY_OPERATION_STORAGE_PREFIX}${ownerSessionId}:${encodeURIComponent(appId)}:${encodeURIComponent(threadId)}`;
}

function readPendingReplyOperation(appId: string, threadId: string): PendingReplyOperation | null {
  const ownerSessionId = getOwnerReplySessionId();
  if (!ownerSessionId) throw new Error("Reply operation storage is unavailable for this authentication session");
  const raw = window.sessionStorage.getItem(replyOperationStorageKey(ownerSessionId, appId, threadId));
  if (!raw) return null;
  let value: Partial<PendingReplyOperation>;
  try {
    value = JSON.parse(raw) as Partial<PendingReplyOperation>;
  } catch {
    throw new Error("Stored reply operation is unreadable; refusing to create a replacement key");
  }
  if (
    value.ownerSessionId !== ownerSessionId ||
    value.appId !== appId ||
    value.threadId !== threadId ||
    typeof value.idempotencyKey !== "string" ||
    value.idempotencyKey.trim() === "" ||
    value.idempotencyKey.length > 150 ||
    typeof value.body !== "string" ||
    value.body.trim() === "" ||
    value.body.length > 10000
  ) {
    throw new Error("Stored reply operation is invalid; refusing to create a replacement key");
  }
  return value as PendingReplyOperation;
}

function savePendingReplyOperation(operation: PendingReplyOperation): boolean {
  try {
    if (getOwnerReplySessionId() !== operation.ownerSessionId) return false;
    window.sessionStorage.setItem(
      replyOperationStorageKey(operation.ownerSessionId, operation.appId, operation.threadId),
      JSON.stringify(operation)
    );
    return true;
  } catch {
    return false;
  }
}

function clearPendingReplyOperation(operation: PendingReplyOperation): boolean {
  try {
    const key = replyOperationStorageKey(operation.ownerSessionId, operation.appId, operation.threadId);
    const current = window.sessionStorage.getItem(key);
    if (!current) return true;
    const stored = JSON.parse(current) as Partial<PendingReplyOperation>;
    if (stored.idempotencyKey === operation.idempotencyKey) {
      window.sessionStorage.removeItem(key);
      return window.sessionStorage.getItem(key) === null;
    }
    return false;
  } catch {
    // A retained receipt key is safe to replay; never replace it with a fresh key.
    return false;
  }
}

function hasRoomForPendingReplyOperation(): boolean {
  try {
    let pendingCount = 0;
    for (let index = 0; index < window.sessionStorage.length; index += 1) {
      if (window.sessionStorage.key(index)?.startsWith(REPLY_OPERATION_STORAGE_PREFIX)) pendingCount += 1;
    }
    return pendingCount < MAX_PENDING_REPLY_OPERATIONS;
  } catch {
    return false;
  }
}

function createReplyIdempotencyKey(): string {
  const bytes = new Uint8Array(24);
  window.crypto.getRandomValues(bytes);
  return `owner-reply-v1-${Array.from(bytes, (byte) => byte.toString(16).padStart(2, "0")).join("")}`;
}

interface FetchTarget {
  cursor?: string | null;
  targetIndex: number;
}

export function SupportConversations({ appId }: { appId: string }) {
  const [filter, setFilter] = useState<FilterState>("all");

  // Single current thread page payload & cursor history (bounded state)
  const [currentThreadPage, setCurrentThreadPage] = useState<OwnerThreadListResponse | null>(null);
  const [threadsPageIndex, setThreadsPageIndex] = useState<number>(0);
  const currentThreadPageRef = useRef<OwnerThreadListResponse | null>(null);
  const threadsPageIndexRef = useRef(0);
  const pendingThreadPageRef = useRef<FetchTarget | null>(null);
  const [threadsCursorHistory, setThreadsCursorHistory] = useState<(string | null)[]>([null]);
  const threadsCursorHistoryRef = useRef<(string | null)[]>([null]);
  const [threadsLoading, setThreadsLoading] = useState<boolean>(false);
  const [threadsError, setThreadsError] = useState<string | null>(null);
  const [threadsRetryTarget, setThreadsRetryTarget] = useState<FetchTarget | null>(null);

  // Selected thread & single current message page payload & cursor history (bounded state)
  const [selectedThread, setSelectedThread] = useState<OwnerThreadSummary | null>(null);
  const [currentMessagePage, setCurrentMessagePage] = useState<OwnerMessageListResponse | null>(null);
  const [messagesPageIndex, setMessagesPageIndex] = useState<number>(0);
  const [messagesCursorHistory, setMessagesCursorHistory] = useState<(string | null)[]>([null]);
  const messagesCursorHistoryRef = useRef<(string | null)[]>([null]);
  const [messagesLoading, setMessagesLoading] = useState<boolean>(false);
  const [messagesError, setMessagesError] = useState<string | null>(null);
  const [messagesRetryTarget, setMessagesRetryTarget] = useState<FetchTarget | null>(null);

  // Status toggle state
  const [statusConfirmClose, setStatusConfirmClose] = useState<boolean>(false);
  const [isUpdatingStatus, setIsUpdatingStatus] = useState<boolean>(false);
  const [statusError, setStatusError] = useState<string | null>(null);
  const [statusRetryTarget, setStatusRetryTarget] = useState<{
    appId: string;
    threadId: string;
    targetIsOpen: boolean;
  } | null>(null);
  const activeStatusScopeRef = useRef<{
    appId: string;
    threadId: string;
    isOpenFilter?: boolean;
    requestId: number;
  } | null>(null);
  const statusRequestIdRef = useRef(0);
  const statusPendingRef = useRef(false);

  // Reply form state
  const [replyText, setReplyText] = useState<string>("");
  const [isReplying, setIsReplying] = useState<boolean>(false);
  const [replyError, setReplyError] = useState<string | null>(null);
  const [replySuccess, setReplySuccess] = useState<boolean>(false);
  const [replyStorageUnavailable, setReplyStorageUnavailable] = useState<boolean>(false);
  const [sentReceipt, setSentReceipt] = useState<OwnerMessageItem | null>(null);
  const [replyOperation, setReplyOperation] = useState<PendingReplyOperation | null>(null);
  const activeReplyScopeRef = useRef<{ appId: string; threadId: string; requestId: number; operationKey: string } | null>(null);
  const confirmedRepliesRef = useRef(new Map<string, { operation: PendingReplyOperation; receipt: OwnerMessageItem }>());
  const replyRequestIdRef = useRef(0);
  const replyPendingRef = useRef(false);
  const replyInFlightKeysRef = useRef(new Set<string>());
  const visibleReplyScopeRef = useRef<{ appId: string; threadId: string; operationKey: string | null } | null>(null);
  const replyAuthGenerationRef = useRef(0);

  useEffect(() => {
    const clearReplyStateForAuthBoundary = () => {
      replyAuthGenerationRef.current += 1;
      replyRequestIdRef.current += 1;
      replyPendingRef.current = false;
      activeReplyScopeRef.current = null;
      confirmedRepliesRef.current.clear();
      visibleReplyScopeRef.current = null;
      setReplyText("");
      setIsReplying(false);
      setReplyError(null);
      setReplySuccess(false);
      setReplyStorageUnavailable(false);
      setSentReceipt(null);
      setReplyOperation(null);
    };
    window.addEventListener("fleet-hub-auth-session-changed", clearReplyStateForAuthBoundary);
    return () => window.removeEventListener("fleet-hub-auth-session-changed", clearReplyStateForAuthBoundary);
  }, []);

  // Concurrency refs
  const threadsAbortRef = useRef<AbortController | null>(null);
  const threadsReqIdRef = useRef<number>(0);

  const messagesAbortRef = useRef<AbortController | null>(null);
  const messagesReqIdRef = useRef<number>(0);

  const loadThreads = useCallback(
    (cursor?: string | null, targetIndex = 0) => {
      if (threadsAbortRef.current) {
        threadsAbortRef.current.abort();
      }
      const controller = new AbortController();
      threadsAbortRef.current = controller;
      const reqId = ++threadsReqIdRef.current;
      pendingThreadPageRef.current = { cursor, targetIndex };

      setThreadsLoading(true);
      setThreadsError(null);

      const isOpenParam = filter === "all" ? undefined : filter === "open" ? true : false;
      const path = buildThreadsPath(appId, 20, isOpenParam, cursor);

      request<unknown>(path, { signal: controller.signal })
        .then((raw) => {
          if (controller.signal.aborted || reqId !== threadsReqIdRef.current) return;
          const visited = threadsCursorHistoryRef.current.slice(0, targetIndex);
          const data = validatePageResponse<OwnerThreadSummary>(raw, cursor, visited);
          const nextHistory = [...threadsCursorHistoryRef.current.slice(0, targetIndex)];
          nextHistory[targetIndex] = cursor ?? null;
          threadsCursorHistoryRef.current = nextHistory;
          setThreadsCursorHistory(nextHistory);
          currentThreadPageRef.current = data;
          threadsPageIndexRef.current = targetIndex;
          pendingThreadPageRef.current = null;
          setCurrentThreadPage(data);
          setThreadsPageIndex(targetIndex);
          setThreadsLoading(false);
          setThreadsRetryTarget(null);
        })
        .catch((err) => {
          if (
            controller.signal.aborted ||
            (err as any)?.name === "AbortError" ||
            (err instanceof DOMException && err.name === "AbortError")
          ) {
            return;
          }
          if (reqId !== threadsReqIdRef.current) return;
          pendingThreadPageRef.current = null;
          setThreadsLoading(false);
          setThreadsError(
            err instanceof ApiError
              ? err.message
              : err instanceof Error
              ? err.message
              : "Не удалось загрузить обращения"
          );
          setThreadsRetryTarget({ cursor, targetIndex });
        });
    },
    [appId, filter]
  );

  const invalidateThreadsRequest = () => {
    threadsAbortRef.current?.abort();
    threadsAbortRef.current = null;
    threadsReqIdRef.current += 1;
    pendingThreadPageRef.current = null;
    setThreadsLoading(false);
  };

  useEffect(() => {
    // Reset threads and messages on appId or filter change
    if (threadsAbortRef.current) threadsAbortRef.current.abort();
    if (messagesAbortRef.current) messagesAbortRef.current.abort();

    setSelectedThread(null);
    setCurrentMessagePage(null);
    setMessagesPageIndex(0);
    messagesCursorHistoryRef.current = [null];
    setMessagesCursorHistory([null]);
    setMessagesError(null);
    setMessagesRetryTarget(null);

    setCurrentThreadPage(null);
    setThreadsPageIndex(0);
    currentThreadPageRef.current = null;
    threadsPageIndexRef.current = 0;
    pendingThreadPageRef.current = null;
    threadsCursorHistoryRef.current = [null];
    setThreadsCursorHistory([null]);
    setThreadsError(null);
    setThreadsRetryTarget(null);

    setStatusConfirmClose(false);
    setIsUpdatingStatus(false);
    setStatusError(null);
    setStatusRetryTarget(null);
    statusRequestIdRef.current += 1;
    statusPendingRef.current = false;
    activeStatusScopeRef.current = null;

    setReplyText("");
    setIsReplying(false);
    setReplyError(null);
    setReplySuccess(false);
    setReplyStorageUnavailable(false);
    setSentReceipt(null);
    setReplyOperation(null);
    replyRequestIdRef.current += 1;
    replyPendingRef.current = false;
    activeReplyScopeRef.current = null;
    visibleReplyScopeRef.current = null;

    loadThreads(undefined, 0);

    return () => {
      threadsAbortRef.current?.abort();
      messagesAbortRef.current?.abort();
    };
  }, [appId, filter, loadThreads]);

  const loadMessages = useCallback(
    (threadId: string, cursor?: string | null, targetIndex = 0) => {
      if (messagesAbortRef.current) {
        messagesAbortRef.current.abort();
      }
      const controller = new AbortController();
      messagesAbortRef.current = controller;
      const reqId = ++messagesReqIdRef.current;

      setMessagesLoading(true);
      setMessagesError(null);

      const path = buildMessagesPath(appId, threadId, 20, cursor);

      request<unknown>(path, { signal: controller.signal })
        .then((raw) => {
          if (controller.signal.aborted || reqId !== messagesReqIdRef.current) return;
          const visited = messagesCursorHistoryRef.current.slice(0, targetIndex);
          const data = validatePageResponse<OwnerMessageItem>(raw, cursor, visited);
          const nextHistory = [...messagesCursorHistoryRef.current.slice(0, targetIndex)];
          nextHistory[targetIndex] = cursor ?? null;
          messagesCursorHistoryRef.current = nextHistory;
          setMessagesCursorHistory(nextHistory);
          setCurrentMessagePage(data);
          setMessagesPageIndex(targetIndex);
          setMessagesLoading(false);
          setMessagesRetryTarget(null);
        })
        .catch((err) => {
          if (
            controller.signal.aborted ||
            (err as any)?.name === "AbortError" ||
            (err instanceof DOMException && err.name === "AbortError")
          ) {
            return;
          }
          if (reqId !== messagesReqIdRef.current) return;
          setMessagesLoading(false);
          setMessagesError(
            err instanceof ApiError
              ? err.message
              : err instanceof Error
              ? err.message
              : "Не удалось загрузить сообщения"
          );
          setMessagesRetryTarget({ cursor, targetIndex });
        });
    },
    [appId]
  );

  useEffect(() => {
    if (messagesAbortRef.current) {
      messagesAbortRef.current.abort();
    }
    setCurrentMessagePage(null);
    setMessagesPageIndex(0);
    messagesCursorHistoryRef.current = [null];
    setMessagesCursorHistory([null]);
    setMessagesError(null);
    setMessagesRetryTarget(null);

    setStatusConfirmClose(false);
    setIsUpdatingStatus(false);
    setStatusError(null);
    setStatusRetryTarget(null);
    statusRequestIdRef.current += 1;
    statusPendingRef.current = false;
    activeStatusScopeRef.current = null;

    setReplyText("");
    setIsReplying(false);
    setReplyError(null);
    setReplySuccess(false);
    setSentReceipt(null);
    setReplyOperation(null);
    replyRequestIdRef.current += 1;
    replyPendingRef.current = false;
    activeReplyScopeRef.current = null;

    if (!selectedThread) {
      visibleReplyScopeRef.current = null;
      setReplyOperation(null);
      setIsReplying(false);
    } else {
      let pendingOperation: PendingReplyOperation | null = null;
      try {
        pendingOperation = readPendingReplyOperation(appId, selectedThread.id);
        setReplyStorageUnavailable(false);
      } catch (error) {
        setReplyStorageUnavailable(true);
        setReplyError(error instanceof Error ? error.message : "Не удалось безопасно прочитать ожидающий ответ");
      }
      const operationIsInFlight = pendingOperation !== null && replyInFlightKeysRef.current.has(replyOperationIdentity(pendingOperation));
      visibleReplyScopeRef.current = {
        appId,
        threadId: selectedThread.id,
        operationKey: pendingOperation?.idempotencyKey ?? null,
      };
      setReplyOperation(pendingOperation);
      setReplyText(pendingOperation?.body ?? "");
      setIsReplying(operationIsInFlight);
      if (pendingOperation) {
        setReplyError(operationIsInFlight ? null : "Результат предыдущей отправки неизвестен. Повторите тот же ответ.");
      }
    }

    if (!selectedThread) return;

    loadMessages(selectedThread.id, undefined, 0);

    return () => {
      messagesAbortRef.current?.abort();
    };
  }, [selectedThread?.id, loadMessages]);

  const currentThreads = Array.isArray(currentThreadPage?.items) ? currentThreadPage.items : [];

  const isThreadsNextDisabled =
    !currentThreadPage?.has_more ||
    !currentThreadPage?.next_cursor ||
    threadsLoading ||
    currentThreadPage.next_cursor === (threadsCursorHistory[threadsPageIndex] ?? null) ||
    threadsCursorHistory.slice(0, threadsPageIndex).includes(currentThreadPage.next_cursor);

  const handleThreadsNext = () => {
    if (isThreadsNextDisabled || !currentThreadPage?.next_cursor) return;
    loadThreads(currentThreadPage.next_cursor, threadsPageIndex + 1);
  };

  const handleThreadsBack = () => {
    if (threadsPageIndex > 0 && !threadsLoading) {
      const prevIndex = threadsPageIndex - 1;
      const prevCursor = threadsCursorHistory[prevIndex] ?? null;
      loadThreads(prevCursor, prevIndex);
    }
  };

  const handleThreadsRetry = () => {
    if (threadsRetryTarget) {
      loadThreads(threadsRetryTarget.cursor, threadsRetryTarget.targetIndex);
    } else {
      loadThreads(undefined, 0);
    }
  };

  const handleSelectThread = (thread: unknown) => {
    if (!isValidThreadSummary(thread)) return;
    if (selectedThread?.id === thread.id) return;
    statusRequestIdRef.current += 1;
    statusPendingRef.current = false;
    activeStatusScopeRef.current = null;
    replyRequestIdRef.current += 1;
    replyPendingRef.current = false;
    activeReplyScopeRef.current = null;
    setSelectedThread(thread);
  };

  const handleFilterChange = (nextFilter: FilterState) => {
    if (nextFilter === filter) return;
    statusRequestIdRef.current += 1;
    statusPendingRef.current = false;
    activeStatusScopeRef.current = null;
    replyRequestIdRef.current += 1;
    replyPendingRef.current = false;
    activeReplyScopeRef.current = null;
    setFilter(nextFilter);
  };

  const currentMessages = Array.isArray(currentMessagePage?.items) ? currentMessagePage.items : [];

  const isMessagesNextDisabled =
    !selectedThread ||
    !currentMessagePage?.has_more ||
    !currentMessagePage?.next_cursor ||
    messagesLoading ||
    currentMessagePage.next_cursor === (messagesCursorHistory[messagesPageIndex] ?? null) ||
    messagesCursorHistory.slice(0, messagesPageIndex).includes(currentMessagePage.next_cursor);

  const handleMessagesNext = () => {
    if (!selectedThread || isMessagesNextDisabled || !currentMessagePage?.next_cursor) return;
    loadMessages(selectedThread.id, currentMessagePage.next_cursor, messagesPageIndex + 1);
  };

  const handleMessagesBack = () => {
    if (!selectedThread || messagesPageIndex === 0 || messagesLoading) return;
    const prevIndex = messagesPageIndex - 1;
    const prevCursor = messagesCursorHistory[prevIndex] ?? null;
    loadMessages(selectedThread.id, prevCursor, prevIndex);
  };

  const handleMessagesRetry = () => {
    if (!selectedThread) return;
    if (messagesRetryTarget) {
      loadMessages(selectedThread.id, messagesRetryTarget.cursor, messagesRetryTarget.targetIndex);
    } else {
      loadMessages(selectedThread.id, undefined, 0);
    }
  };

  const handleUpdateStatus = async (targetIsOpen: boolean) => {
    if (!selectedThread || statusPendingRef.current) return;
    const targetAppId = appId;
    const targetThreadId = selectedThread.id;
    const currentIsOpenFilter = filter === "all" ? undefined : filter === "open" ? true : false;
    const requestId = ++statusRequestIdRef.current;
    statusPendingRef.current = true;
    activeStatusScopeRef.current = {
      appId: targetAppId,
      threadId: targetThreadId,
      isOpenFilter: currentIsOpenFilter,
      requestId,
    };
    setIsUpdatingStatus(true);
    setStatusError(null);
    setStatusRetryTarget(null);

    try {
      const updated = await request<{ id: string; is_open: boolean }>(
        `/api/v1/apps/${encodeURIComponent(targetAppId)}/support/threads/${encodeURIComponent(targetThreadId)}`,
        {
          method: "PATCH",
          body: JSON.stringify({ is_open: targetIsOpen }),
        }
      );

      if (
        activeStatusScopeRef.current?.appId !== targetAppId ||
        activeStatusScopeRef.current?.threadId !== targetThreadId ||
        activeStatusScopeRef.current?.isOpenFilter !== currentIsOpenFilter ||
        activeStatusScopeRef.current?.requestId !== requestId
      ) {
        return;
      }

      if (updated?.id !== targetThreadId || typeof updated.is_open !== "boolean") {
        throw new Error("Некорректное подтверждение смены статуса обращения");
      }
      const nextIsOpen = updated.is_open;
      setSelectedThread((prev) =>
        prev && prev.id === targetThreadId ? { ...prev, is_open: nextIsOpen } : prev
      );
      const isIncludedByFilter = currentIsOpenFilter === undefined || currentIsOpenFilter === nextIsOpen;
      const wasOnCurrentPage = currentThreadPageRef.current?.items.some((item) => item.id === targetThreadId) ?? false;
      const pendingPage = pendingThreadPageRef.current;
      invalidateThreadsRequest();
      if (pendingPage) {
        // The owner already requested another cursor while this PATCH was pending.
        // Reissue that page after the ACK so a pre-ACK GET cannot restore old status.
        loadThreads(pendingPage.cursor, pendingPage.targetIndex);
      } else if (!isIncludedByFilter) {
        const page = currentThreadPageRef.current;
        const next = page
          ? { ...page, items: page.items.filter((item) => item.id !== targetThreadId) }
          : null;
        currentThreadPageRef.current = next;
        setCurrentThreadPage(next);
      } else if (!wasOnCurrentPage) {
        const pageIndex = threadsPageIndexRef.current;
        const pageCursor = threadsCursorHistoryRef.current[pageIndex] ?? null;
        loadThreads(pageCursor, pageIndex);
      } else {
        const page = currentThreadPageRef.current;
        const next = page
          ? { ...page, items: page.items.map((item) => item.id === targetThreadId ? { ...item, is_open: nextIsOpen } : item) }
          : null;
        currentThreadPageRef.current = next;
        setCurrentThreadPage(next);
      }
      setStatusConfirmClose(false);
    } catch (err) {
      if (
        activeStatusScopeRef.current?.appId !== targetAppId ||
        activeStatusScopeRef.current?.threadId !== targetThreadId ||
        activeStatusScopeRef.current?.isOpenFilter !== currentIsOpenFilter ||
        activeStatusScopeRef.current?.requestId !== requestId
      ) {
        return;
      }
      setStatusError(
        err instanceof ApiError
          ? err.message
          : err instanceof Error
          ? err.message
          : "Не удалось изменить статус обращения"
      );
      setStatusRetryTarget({ appId: targetAppId, threadId: targetThreadId, targetIsOpen });
    } finally {
      if (
        activeStatusScopeRef.current?.appId === targetAppId &&
        activeStatusScopeRef.current?.threadId === targetThreadId &&
        activeStatusScopeRef.current?.isOpenFilter === currentIsOpenFilter &&
        activeStatusScopeRef.current?.requestId === requestId
      ) {
        statusPendingRef.current = false;
        setIsUpdatingStatus(false);
      }
    }
  };

  const handleRetryStatus = () => {
    if (statusRetryTarget) {
      handleUpdateStatus(statusRetryTarget.targetIsOpen);
    }
  };

  const handleSendReply = async (textToSend: string) => {
    if (!textToSend || replyPendingRef.current || !selectedThread) return;
    const targetAppId = appId;
    const targetThreadId = selectedThread.id;
    const confirmedReply = replyOperation ? confirmedRepliesRef.current.get(replyOperationIdentity(replyOperation)) : undefined;
    if (
      confirmedReply?.operation.appId === targetAppId &&
      confirmedReply.operation.threadId === targetThreadId &&
      replyOperation?.idempotencyKey === confirmedReply.operation.idempotencyKey &&
      getOwnerReplySessionId() === confirmedReply.operation.ownerSessionId
    ) {
      if (clearPendingReplyOperation(confirmedReply.operation)) {
        confirmedRepliesRef.current.delete(replyOperationIdentity(confirmedReply.operation));
        const visibleScope = visibleReplyScopeRef.current;
        if (
          visibleScope?.appId === targetAppId &&
          visibleScope.threadId === targetThreadId &&
          visibleScope.operationKey === confirmedReply.operation.idempotencyKey
        ) {
          setReplyText("");
          setReplyOperation(null);
          setReplySuccess(true);
          setSentReceipt(confirmedReply.receipt);
          setReplyError(null);
          setReplyStorageUnavailable(false);
          setIsReplying(false);
          visibleReplyScopeRef.current = { appId: targetAppId, threadId: targetThreadId, operationKey: null };
        }
      } else {
        setReplyStorageUnavailable(true);
        setReplyError("Ответ уже подтверждён сервером. Безопасный повтор нельзя завершить, пока хранилище недоступно.");
      }
      return;
    }

    let operation: PendingReplyOperation | null;
    try {
      operation = readPendingReplyOperation(targetAppId, targetThreadId);
      setReplyStorageUnavailable(false);
    } catch (error) {
      const knownOperation = replyOperation;
      const visibleScope = visibleReplyScopeRef.current;
      const knownOperationMatches =
        knownOperation?.appId === targetAppId &&
        knownOperation.threadId === targetThreadId &&
        knownOperation.body === textToSend &&
        getOwnerReplySessionId() === knownOperation.ownerSessionId &&
        visibleScope?.appId === targetAppId &&
        visibleScope.threadId === targetThreadId &&
        visibleScope.operationKey === knownOperation.idempotencyKey;
      if (!knownOperationMatches) {
        setReplyStorageUnavailable(true);
        setReplyError(error instanceof Error ? error.message : "Не удалось безопасно прочитать ожидающий ответ");
        return;
      }
      // This tab already persisted the operation before its first POST. Reuse
      // its exact key/body while storage is temporarily unreadable.
      operation = knownOperation;
      setReplyStorageUnavailable(true);
    }
    if (!operation && replyOperation?.appId === targetAppId && replyOperation.threadId === targetThreadId) {
      if (getOwnerReplySessionId() !== replyOperation.ownerSessionId || !savePendingReplyOperation(replyOperation)) {
        setReplyStorageUnavailable(true);
        setReplyError("Не удалось восстановить сохранённый ключ ответа. Ответ не отправлен.");
        return;
      }
      operation = replyOperation;
    }
    if (!operation && textToSend.length > 10000) {
      setReplyError("Текст ответа не должен превышать 10000 символов");
      return;
    }
    if (!operation) {
      const ownerSessionId = getOwnerReplySessionId();
      if (!ownerSessionId) {
        setReplyStorageUnavailable(true);
        setReplyError("Не удалось сохранить безопасный ключ ответа. Войдите снова перед отправкой.");
        return;
      }
      if (!hasRoomForPendingReplyOperation()) {
        setReplyError("Не удалось сохранить безопасный ключ ответа. Завершите ожидающий ответ перед новой отправкой.");
        return;
      }
      try {
        operation = {
          ownerSessionId,
          appId: targetAppId,
          threadId: targetThreadId,
          idempotencyKey: createReplyIdempotencyKey(),
          body: textToSend,
        };
      } catch {
        setReplyError("Не удалось подготовить безопасный повтор отправки ответа");
        return;
      }
      if (!savePendingReplyOperation(operation)) {
        setReplyStorageUnavailable(true);
        setReplyError("Не удалось сохранить ключ ответа для безопасного повтора. Ответ не отправлен.");
        return;
      }
    }
    setReplyStorageUnavailable(false);
    const operationKey = replyOperationIdentity(operation);
    if (replyInFlightKeysRef.current.has(operationKey)) return;
    const requestId = ++replyRequestIdRef.current;
    const authGenerationAtSend = replyAuthGenerationRef.current;
    replyPendingRef.current = true;
    replyInFlightKeysRef.current.add(operationKey);
    activeReplyScopeRef.current = { appId: targetAppId, threadId: targetThreadId, requestId, operationKey: operation.idempotencyKey };
    visibleReplyScopeRef.current = { appId: targetAppId, threadId: targetThreadId, operationKey: operation.idempotencyKey };
    setReplyOperation(operation);
    setReplyText(operation.body);
    setIsReplying(true);
    setReplyError(null);
    setReplySuccess(false);

    try {
      const receipt = await request<OwnerMessageItem>(
        `/api/v1/apps/${encodeURIComponent(targetAppId)}/support/threads/${encodeURIComponent(targetThreadId)}/reply`,
        {
          method: "POST",
          headers: { "Idempotency-Key": operation.idempotencyKey },
          body: JSON.stringify({ body: operation.body }),
        }
      );

      if (!isValidMessageItem(receipt) || receipt.sender !== "owner" || receipt.body !== operation.body) {
        throw new Error("Некорректное подтверждение отправки ответа");
      }

      if (authGenerationAtSend === replyAuthGenerationRef.current) {
        confirmedRepliesRef.current.set(operationKey, { operation, receipt });
      }
      const operationCleared = clearPendingReplyOperation(operation);
      if (operationCleared) confirmedRepliesRef.current.delete(operationKey);
      const visibleScope = visibleReplyScopeRef.current;
      const isActiveRequest =
        activeReplyScopeRef.current?.appId === targetAppId &&
        activeReplyScopeRef.current.threadId === targetThreadId &&
        activeReplyScopeRef.current.requestId === requestId &&
        activeReplyScopeRef.current.operationKey === operation.idempotencyKey;
      const isVisibleOperation =
        visibleScope?.appId === targetAppId &&
        visibleScope.threadId === targetThreadId &&
        visibleScope.operationKey === operation.idempotencyKey;
      if (isActiveRequest || isVisibleOperation) {
        if (operationCleared) {
          setReplyText("");
          setReplyOperation(null);
          setReplySuccess(true);
          setSentReceipt(receipt);
          setReplyError(null);
          setIsReplying(false);
          visibleReplyScopeRef.current = { appId: targetAppId, threadId: targetThreadId, operationKey: null };
        } else {
          // The server confirmed this reply, but the stored key still needs clearing.
          // Keep new sends locked until replay can safely finish that cleanup.
          setReplyOperation(operation);
          setReplyText(operation.body);
          setReplySuccess(false);
          setReplyStorageUnavailable(true);
          setReplyError("Ответ подтверждён, но не удалось завершить безопасный повтор. Повторите после восстановления хранилища.");
        }
      }
    } catch (err) {
      let stillPending: PendingReplyOperation | null = null;
      let pendingReadFailed = false;
      let pendingReadError: unknown;
      try {
        stillPending = readPendingReplyOperation(targetAppId, targetThreadId);
      } catch (storageError) {
        pendingReadFailed = true;
        pendingReadError = storageError;
      }
      const visibleScope = visibleReplyScopeRef.current;
      const operationStillCurrent =
        visibleScope?.appId === targetAppId &&
        visibleScope.threadId === targetThreadId &&
        visibleScope.operationKey === operation.idempotencyKey &&
        getOwnerReplySessionId() === operation.ownerSessionId;
      if (
        operationStillCurrent &&
        (stillPending?.idempotencyKey === operation.idempotencyKey || pendingReadFailed || stillPending === null)
      ) {
        setReplyOperation(operation);
        setReplyText(operation.body);
        setReplySuccess(false);
        setReplyStorageUnavailable(pendingReadFailed);
        setReplyError(
          pendingReadFailed
            ? `Результат отправки неизвестен: ${err instanceof Error ? err.message : "ошибка ответа"}. Хранилище временно недоступно (${pendingReadError instanceof Error ? pendingReadError.message : "ошибка чтения"}); повтор использует тот же ответ.`
            : err instanceof ApiError
            ? err.message
            : err instanceof Error
            ? err.message
            : "Не удалось отправить ответ"
        );
      }
    } finally {
      replyInFlightKeysRef.current.delete(operationKey);
      const visibleScope = visibleReplyScopeRef.current;
      if (
        activeReplyScopeRef.current?.appId === targetAppId &&
        activeReplyScopeRef.current?.threadId === targetThreadId &&
        activeReplyScopeRef.current?.requestId === requestId &&
        activeReplyScopeRef.current?.operationKey === operation.idempotencyKey
      ) {
        replyPendingRef.current = false;
        activeReplyScopeRef.current = null;
      }
      if (
        visibleScope?.appId === targetAppId &&
        visibleScope.threadId === targetThreadId &&
        visibleScope.operationKey === operation.idempotencyKey
      ) {
        setIsReplying(false);
      }
    }
  };

  const handleReplySubmit = (e: React.FormEvent) => {
    e.preventDefault();
    handleSendReply(replyText.trim());
  };

  const handleRetryReply = () => {
    if (replyStorageUnavailable && !replyOperation && selectedThread) {
      try {
        const pending = readPendingReplyOperation(appId, selectedThread.id);
        setReplyStorageUnavailable(false);
        if (pending) {
          setReplyOperation(pending);
          setReplyText(pending.body);
          setReplyError("Результат предыдущей отправки неизвестен. Повторите тот же ответ.");
          return;
        }
        setReplyError(null);
        if (replyText.trim()) handleSendReply(replyText.trim());
      } catch (error) {
        setReplyError(error instanceof Error ? error.message : "Не удалось безопасно прочитать ожидающий ответ");
      }
      return;
    }
    handleSendReply(replyOperation?.body ?? replyText.trim());
  };

  return (
    <div className="support-conversations" style={{ marginTop: 16 }}>
      <section aria-label="Список обращений">
        <div
          role="group"
          aria-label="Фильтр по статусу"
          style={{ display: "flex", gap: 8, marginBottom: 16 }}
        >
          <Button
            variant={filter === "all" ? "primary" : "secondary"}
            size="sm"
            aria-pressed={filter === "all"}
            onClick={() => handleFilterChange("all")}
          >
            Все
          </Button>
          <Button
            variant={filter === "open" ? "primary" : "secondary"}
            size="sm"
            aria-pressed={filter === "open"}
            onClick={() => handleFilterChange("open")}
          >
            Открытые
          </Button>
          <Button
            variant={filter === "closed" ? "primary" : "secondary"}
            size="sm"
            aria-pressed={filter === "closed"}
            onClick={() => handleFilterChange("closed")}
          >
            Закрытые
          </Button>
        </div>

        {threadsError && (
          <p role="alert" className="error-text">
            {threadsError}{" "}
            <Button variant="secondary" size="sm" onClick={handleThreadsRetry}>
              Повторить
            </Button>
          </p>
        )}

        {threadsLoading && <p role="status">Загрузка обращений…</p>}

        {!threadsLoading && !threadsError && currentThreads.length === 0 && (
          <p>Нет обращений.</p>
        )}

        <div style={{ display: "grid", gap: 8 }}>
          {currentThreads.map((t, idx) => {
            if (!isValidThreadSummary(t)) {
              return (
                <Card key={`corrupt-${idx}`} style={{ padding: 14, color: "var(--omni-text-secondary)" }}>
                  <em>Не удалось отобразить обращение</em>
                </Card>
              );
            }
            const threadId = t.id;
            const isSelected = selectedThread?.id === threadId;
            return (
              <Card
                key={threadId}
                role="button"
                tabIndex={0}
                aria-selected={isSelected}
                onClick={() => handleSelectThread(t)}
                onKeyDown={(e) => {
                  if (e.key === "Enter" || e.key === " ") {
                    e.preventDefault();
                    handleSelectThread(t);
                  }
                }}
                style={{
                  cursor: "pointer",
                  padding: 14,
                  border: isSelected
                    ? "1px solid var(--omni-primary)"
                    : "1px solid var(--omni-border-subtle)",
                  background: isSelected
                    ? "var(--omni-surface-raised)"
                    : "var(--omni-surface)",
                }}
              >
                <div
                  style={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "flex-start",
                    marginBottom: 6,
                  }}
                >
                  <strong style={{ fontSize: 15 }}>{t.subject}</strong>
                  <Badge variant={t.is_open ? "success" : "neutral"} size="sm">
                    {t.is_open ? "Открыто" : "Закрыто"}
                  </Badge>
                </div>
                <div style={{ fontSize: 13, color: "var(--omni-text-secondary)" }}>
                  <div>Клиент: {t.external_customer_id}</div>
                  <div style={{ marginTop: 2 }}>
                    Создано: {formatTimestamp(t.created_at)} · Обновлено: {formatTimestamp(t.updated_at)}
                  </div>
                </div>
              </Card>
            );
          })}
        </div>

        <div
          style={{
            display: "flex",
            gap: 8,
            alignItems: "center",
            marginTop: 14,
          }}
        >
          <Button
            variant="secondary"
            size="sm"
            disabled={threadsPageIndex === 0 || threadsLoading}
            onClick={handleThreadsBack}
          >
            Назад
          </Button>
          <Button
            variant="secondary"
            size="sm"
            disabled={isThreadsNextDisabled}
            onClick={handleThreadsNext}
          >
            Далее
          </Button>
          <span style={{ fontSize: 13, color: "var(--omni-text-secondary)" }}>
            Страница {threadsPageIndex + 1}
          </span>
        </div>
      </section>

      {selectedThread && isValidThreadSummary(selectedThread) && (
        <section
          aria-label="Сообщения обращения"
          style={{
            marginTop: 24,
            paddingTop: 16,
            borderTop: "1px solid var(--omni-border-subtle)",
          }}
        >
          <div style={{ marginBottom: 12 }}>
            <div
              style={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "flex-start",
                gap: 16,
              }}
            >
              <div>
                <h3 style={{ margin: "0 0 4px" }}>
                  Сообщения: {selectedThread.subject}
                </h3>
                <div style={{ fontSize: 13, color: "var(--omni-text-secondary)" }}>
                  Клиент: {selectedThread.external_customer_id} · Статус:{" "}
                  {selectedThread.is_open ? "Открыто" : "Закрыто"}
                </div>
              </div>
              <div>
                {statusConfirmClose ? (
                  <div
                    role="group"
                    aria-label="Подтверждение закрытия обращения"
                    style={{ display: "flex", gap: 8, alignItems: "center" }}
                  >
                    <span style={{ fontSize: 13, color: "var(--omni-text-secondary)" }}>
                      Вы уверены, что хотите закрыть это обращение?
                    </span>
                    <Button
                      variant="secondary"
                      size="sm"
                      disabled={isUpdatingStatus}
                      onClick={() => handleUpdateStatus(false)}
                    >
                      Да, закрыть
                    </Button>
                    <Button
                      variant="ghost"
                      size="sm"
                      disabled={isUpdatingStatus}
                      onClick={() => setStatusConfirmClose(false)}
                    >
                      Отмена
                    </Button>
                  </div>
                ) : (
                  <Button
                    variant="secondary"
                    size="sm"
                    disabled={isUpdatingStatus}
                    onClick={() => {
                      if (selectedThread.is_open) {
                        setStatusConfirmClose(true);
                      } else {
                        handleUpdateStatus(true);
                      }
                    }}
                  >
                    {selectedThread.is_open ? "Закрыть обращение" : "Открыть обращение"}
                  </Button>
                )}
              </div>
            </div>

            {statusError && (
              <p role="alert" className="error-text" style={{ marginTop: 8 }}>
                {statusError}{" "}
                <Button
                  type="button"
                  variant="secondary"
                  size="sm"
                  disabled={isUpdatingStatus}
                  onClick={handleRetryStatus}
                >
                  Повторить
                </Button>
              </p>
            )}
          </div>

          {messagesError && (
            <p role="alert" className="error-text">
              {messagesError}{" "}
              <Button
                variant="secondary"
                size="sm"
                onClick={handleMessagesRetry}
              >
                Повторить
              </Button>
            </p>
          )}

          {messagesLoading && <p role="status">Загрузка сообщений…</p>}

          {!messagesLoading && !messagesError && currentMessages.length === 0 && (
            <p>Нет сообщений.</p>
          )}

          <div style={{ display: "grid", gap: 10 }}>
            {currentMessages.map((msg, idx) => {
              if (!isValidMessageItem(msg)) {
                return (
                  <Card key={`corrupt-msg-${idx}`} style={{ padding: 12, color: "var(--omni-text-secondary)" }}>
                    <em>Не удалось отобразить сообщение</em>
                  </Card>
                );
              }
              const msgId = msg.id;
              return (
                <Card key={msgId} style={{ padding: 12 }}>
                  <div
                    style={{
                      display: "flex",
                      justifyContent: "space-between",
                      marginBottom: 6,
                    }}
                  >
                    <strong>{formatSender(msg.sender)}</strong>
                    <span
                      style={{
                        fontSize: 12,
                        color: "var(--omni-text-secondary)",
                      }}
                    >
                      {formatTimestamp(msg.created_at)}
                    </span>
                  </div>
                  <div
                    style={{
                      whiteSpace: "pre-wrap",
                      wordBreak: "break-word",
                      fontSize: 14,
                    }}
                  >
                    {msg.body}
                  </div>
                </Card>
              );
            })}
          </div>

          <div
            style={{
              display: "flex",
              gap: 8,
              alignItems: "center",
              marginTop: 14,
            }}
          >
            <Button
              variant="secondary"
              size="sm"
              disabled={messagesPageIndex === 0 || messagesLoading}
              onClick={handleMessagesBack}
            >
              Назад
            </Button>
            <Button
              variant="secondary"
              size="sm"
              disabled={isMessagesNextDisabled}
              onClick={handleMessagesNext}
            >
              Далее
            </Button>
            <span style={{ fontSize: 13, color: "var(--omni-text-secondary)" }}>
              Страница {messagesPageIndex + 1}
            </span>
          </div>

          {sentReceipt && isValidMessageItem(sentReceipt) && !currentMessages.some((msg) => msg.id === sentReceipt.id) && (
            <div
              aria-label="Отправленный ответ"
              style={{
                marginTop: 16,
                padding: 12,
                borderRadius: 8,
                background: "var(--omni-surface-raised, #f9fafb)",
                border: "1px solid var(--omni-border-subtle, #e5e7eb)",
              }}
            >
              <div
                style={{
                  fontSize: 13,
                  fontWeight: 600,
                  marginBottom: 8,
                  color: "var(--omni-text-secondary)",
                }}
              >
                Отправленный ответ
              </div>
              <Card key={sentReceipt.id} style={{ padding: 12 }}>
                <div
                  style={{
                    display: "flex",
                    justifyContent: "space-between",
                    marginBottom: 6,
                  }}
                >
                  <strong>{formatSender(sentReceipt.sender)}</strong>
                  <span
                    style={{
                      fontSize: 12,
                      color: "var(--omni-text-secondary)",
                    }}
                  >
                    {formatTimestamp(sentReceipt.created_at)}
                  </span>
                </div>
                <div
                  style={{
                    whiteSpace: "pre-wrap",
                    wordBreak: "break-word",
                    fontSize: 14,
                  }}
                >
                  {sentReceipt.body}
                </div>
              </Card>
            </div>
          )}

          <form
            aria-label="Ответ на обращение"
            onSubmit={handleReplySubmit}
            style={{ marginTop: 24 }}
          >
            <label
              htmlFor="support-reply-body"
              style={{ display: "block", marginBottom: 6, fontWeight: 500, fontSize: 14 }}
            >
              Ответ
            </label>
            <textarea
              id="support-reply-body"
              value={replyText}
              onChange={(e) => {
                setReplyText(e.target.value);
                setReplySuccess(false);
              }}
              placeholder="Введите ответ…"
              disabled={isReplying || replyOperation !== null || replyStorageUnavailable}
              rows={3}
              style={{
                width: "100%",
                padding: "8px 12px",
                borderRadius: 6,
                border: "1px solid var(--omni-border-subtle)",
                background: "var(--omni-surface)",
                color: "var(--omni-text)",
                fontFamily: "inherit",
                fontSize: 14,
                boxSizing: "border-box",
                resize: "vertical",
              }}
            />
            <div style={{ display: "flex", gap: 8, alignItems: "center", marginTop: 8 }}>
              <Button
                variant="primary"
                size="sm"
                type="submit"
                disabled={isReplying || replyOperation !== null || replyStorageUnavailable || !replyText.trim()}
              >
                {isReplying ? "Отправка…" : "Отправить ответ"}
              </Button>
            </div>
            {replySuccess && (
              <p
                role="status"
                style={{ color: "var(--omni-success, #16a34a)", marginTop: 8, fontSize: 13 }}
              >
                Ответ отправлен
              </p>
            )}
            {replyError && (
              <p role="alert" className="error-text" style={{ marginTop: 8 }}>
                {replyError}{" "}
                {replyOperation && (
                  <span>Результат отправки неизвестен; повтор использует тот же ключ и текст.</span>
                )}{" "}
                <Button
                  type="button"
                  variant="secondary"
                  size="sm"
                  disabled={isReplying}
                  onClick={handleRetryReply}
                >
                  Повторить
                </Button>
              </p>
            )}
          </form>
        </section>
      )}
    </div>
  );
}

export default SupportConversations;
